import {Button, Group, Stack, Text} from '@mantine/core';
import assert from 'assert';
import React, {useCallback, useRef, useState} from 'react';

import EntryActions from 'actions/EntryActions';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import EntryDAO from 'dao/EntryDAO';
import FileStates from 'FileStates';
import {TargetEntry} from 'folder-items/FileConstants';
import LinkDAO from 'link-preview/LinkDAO';
import {EntrySummary} from 'notebook/ELNConstants';
import EntrySearchInput from 'notebook/entries/EntrySearchInput';
import {prepareNotePartForSave} from 'notebook/notes/NoteStoreTransforms';
import {NotePart} from 'notebook/util/NoteUtilConstants';
import {FilterKey, Operator} from 'search/SearchConstants';
import {ZINDEX_MODAL} from 'styles/StyleConstants';
import {Item} from 'ui-library/attach-dropdown/ItemSearchInput';
import ModalFooter from 'ui-library/modals/ModalFooter';
import runModal, {RunModalProps} from 'ui-library/runModal';
import APIUtil from 'util/APIUtil';

export interface AddToEntryResult {
  entryId: string;
  url: string;
}

type SelectableEntry = EntrySummary | TargetEntry | Item;

export interface AddToEntryModalProps extends RunModalProps<AddToEntryResult> {
  getContentsToInsert: (
    entryId: string
  ) => Promise<{noteParts?: Array<NotePart>; sourceDocumentApiId?: string}>;
  notePartsContentSourceId?: string;
  /**
   * Entry to pre-select when the modal opens. Callers typically pass the result of
   * `getActiveWorkspaceEntry()` so the entry the user is currently viewing is selected by default.
   */
  initialSelectedEntry?: Item | null;
}

/**
 * Opens {@link AddToEntryModal} via `runModal` and resolves with the result (or `undefined` if dismissed).
 *
 * The modal is rendered at the legacy modal z-index (`ZINDEX_MODAL`, 1050) rather than the default
 * `runModal` z-index (`ZINDEX_FLOATING_UI`, 1060). This is required because the entry search input renders
 * its results dropdown on `document.body` via `AbsoluteDropdownContainer` at z-index 1051
 * (`.absoluteDropdownContainer`). At the default modal z-index the dropdown results would be hidden behind
 * the modal; rendering the modal at 1050 lets the dropdown stack above it, matching the pre-Mantine behavior.
 */
export function openAddToEntryModal(
  contentProps: Omit<AddToEntryModalProps, 'close'>
): Promise<AddToEntryResult | undefined> {
  return runModal(AddToEntryModal, contentProps, {
    title: 'Add to Entry',
    zIndex: ZINDEX_MODAL,
  });
}

/** A bare entry api id pasted directly (not embedded in a URL), e.g. `etr_AbCd1234`. */
const BARE_ENTRY_API_ID_REGEX = /^etr_[A-Za-z0-9]+$/;

/** Returns the trimmed input when it is a bare entry api id, otherwise `null`. */
export function parseBareEntryApiId(input: string): string | null {
  const trimmed = input.trim();
  return BARE_ENTRY_API_ID_REGEX.test(trimmed) ? trimmed : null;
}

/**
 * Whether the input looks like a URL or absolute path that should be resolved to an api id via the
 * `link-previews` endpoint (backed by `benchling/lib/parse_api_id_from_url`). Kept intentionally strict so
 * ordinary name searches (e.g. "assay 1/2") are not sent to the endpoint.
 */
export function looksLikeEntryUrl(input: string): boolean {
  const trimmed = input.trim();
  return /^https?:\/\//i.test(trimmed) || trimmed.startsWith('/');
}

/**
 * Returns the active workspace item as a selectable entry when it is an ELN entry, otherwise `null`.
 * Used to pre-select the entry a user is currently viewing in {@link AddToEntryModal}, since that is
 * the most likely entry they want to add content to.
 */
export function getActiveWorkspaceEntry(): Item | null {
  const activeFileState = FileStates.get().findWhere({active: true});
  if (activeFileState == null || activeFileState.fileType() !== ModelTypeConstants.ENTRY) {
    return null;
  }
  const id = activeFileState.id;
  if (id == null) {
    return null;
  }
  return {id, name: activeFileState.name() ?? null};
}

export function AddToEntryModal({
  getContentsToInsert,
  close,
  notePartsContentSourceId,
  initialSelectedEntry,
}: AddToEntryModalProps): JSX.Element {
  const [selectedEntries, setSelectedEntries] = useState<Array<SelectableEntry>>(
    initialSelectedEntry ? [initialSelectedEntry] : []
  );
  const [query, setQuery] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Guards against a slower URL resolution overwriting a newer query/selection.
  const urlResolutionTokenRef = useRef(0);

  const handleEntriesChange = useCallback((entries: Array<SelectableEntry>) => {
    // A manual selection/removal supersedes any in-flight URL resolution.
    urlResolutionTokenRef.current++;
    setSelectedEntries(entries);
  }, []);

  const selectEntryById = useCallback((entryApiId: string) => {
    setSelectedEntries([{id: entryApiId}]);
    setQuery('');
  }, []);

  // Accept a pasted notebook entry URL (or a bare `etr_...` id) as input for the target entry. A bare id is
  // selected immediately; a URL is resolved to an api id via the `link-previews` endpoint (backed by
  // `benchling/lib/parse_api_id_from_url`), which is tenant- and route-aware and handles legacy/share-link
  // URL shapes. Anything else is treated as a normal text search.
  const handleQueryChange = useCallback(
    (newQuery: string) => {
      // Any new query supersedes a pending URL resolution.
      const token = ++urlResolutionTokenRef.current;

      const bareEntryApiId = parseBareEntryApiId(newQuery);
      if (bareEntryApiId != null) {
        selectEntryById(bareEntryApiId);
        return;
      }

      setQuery(newQuery);

      if (!looksLikeEntryUrl(newQuery)) {
        return;
      }

      LinkDAO.fetchPreviews([newQuery.trim()]).then(
        (response) => {
          // Ignore if a newer query/selection has superseded this resolution.
          if (token !== urlResolutionTokenRef.current) {
            return;
          }
          const entryLink = (response?.data ?? []).find(
            (link) => APIUtil.apiIdToMaybeType(link.id, false) === ModelTypeConstants.ENTRY
          );
          if (entryLink != null) {
            selectEntryById(entryLink.id);
          }
        },
        () => {
          // Ignore resolution failures; the input falls back to a normal text search.
        }
      );
    },
    [selectEntryById]
  );

  const handleSubmit = useCallback(async (): Promise<void> => {
    if (selectedEntries.length === 0) {
      return;
    }

    const selectedEntry = selectedEntries[0];
    if (!selectedEntry.id) {
      return;
    }

    setIsSubmitting(true);

    try {
      const {noteParts, sourceDocumentApiId} = await getContentsToInsert(selectedEntry.id);
      assert(
        noteParts || sourceDocumentApiId,
        'Either note parts or a source document API ID needs to be defined'
      );
      const preparedNoteParts = noteParts?.map((notePart) => prepareNotePartForSave(notePart, {}));
      const result = await EntryDAO.addToEntry({
        entryApiId: selectedEntry.id,
        noteParts: preparedNoteParts,
        sourceDocumentApiId,
        notePartsContentSourceId,
      });

      if (result) {
        // Open the entry to ensure we have the latest version
        EntryActions.open(selectedEntry.id);
        // Navigate to the updated entry
        FileStates.get().openFolderItem(selectedEntry.id);
        close({entryId: selectedEntry.id, url: result.url});
      }
    } finally {
      setIsSubmitting(false);
    }
  }, [selectedEntries, getContentsToInsert, notePartsContentSourceId, close]);

  const canSubmit = selectedEntries.length > 0 && Boolean(selectedEntries[0]?.id) && !isSubmitting;

  return (
    <Stack spacing="md">
      <Text>Select an entry to add the content to:</Text>
      <EntrySearchInput
        selectedEntries={selectedEntries}
        onChange={handleEntriesChange}
        query={query}
        onUpdateQuery={handleQueryChange}
        shouldAllowMultiple={false}
        placeholder="Search by name or paste an entry URL..."
        autoFocus={false}
        additionalFilters={[
          {
            key: FilterKey.IS_FILL_IN_ENTRY,
            operator: Operator.IS_FALSE,
            value: null,
          },
        ]}
      />
      <ModalFooter>
        <Group position="right">
          <Button variant="secondary" onClick={() => close()} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!canSubmit} loading={isSubmitting}>
            Add to Entry
          </Button>
        </Group>
      </ModalFooter>
    </Stack>
  );
}
